import {
  COMMUNITY_NOTIFICATION_TYPE,
  PARTNER_GROUP_CHAT_MESSAGE_TARGET_TYPE,
} from '../constants/communityNotificationTypes';
import { pickNotificationId } from './pickNotificationId';
import { normalizePortalNotificationSource } from './portalNotificationSource';
import { resolvePortalNotificationType } from './portalNotificationRouting';

/** Notification types that mark all channel notifications read via PUT /admin/notifications/read. */
export const CHANNEL_SCOPED_NOTIFICATION_TYPES = new Set([
  COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_CHAT_MESSAGE,
]);

export const pickNotificationChannelId = (source = {}) => {
  const nested = source?.data ?? {};
  const channelId =
    source?.channelId ??
    nested.channelId ??
    source?.channel_id ??
    nested.channel_id ??
    source?.streamChannelId ??
    nested.streamChannelId ??
    null;
  const trimmed = String(channelId ?? '').trim();
  return trimmed || null;
};

export const isChannelScopedNotification = (source = {}) => {
  const normalized = buildPortalNotificationReadSource(source);
  const resolvedType = String(normalized.type || '').toLowerCase();
  const normalizedTarget = String(normalized.targetType || '').toLowerCase();

  if (CHANNEL_SCOPED_NOTIFICATION_TYPES.has(resolvedType)) {
    return true;
  }

  return normalizedTarget === PARTNER_GROUP_CHAT_MESSAGE_TARGET_TYPE;
};

/** Normalize FCM / toast / inbox rows into a read-source payload. */
export const buildPortalNotificationReadSource = (source = {}) => {
  if (typeof source === 'string') {
    return { id: source, notificationId: source };
  }

  const data = source?.data ?? {};
  const merged = { ...data, ...source };
  const resolvedType = resolvePortalNotificationType(merged);

  return {
    ...merged,
    type:
      resolvedType ??
      merged.type ??
      merged.notificationType ??
      data.type ??
      data.notificationType ??
      null,
    targetType: merged.targetType ?? data.targetType ?? null,
    channelId: pickNotificationChannelId(merged),
    notificationId: pickNotificationId(merged),
  };
};

export const resolvePortalNotificationReadAction = (source = {}) => {
  const normalizedSource = buildPortalNotificationReadSource(source);
  const channelId = normalizedSource.channelId ?? pickNotificationChannelId(normalizedSource);

  if (isChannelScopedNotification(normalizedSource) && channelId) {
    return { mode: 'channel', channelId };
  }

  const notificationId = normalizedSource.notificationId ?? pickNotificationId(normalizedSource);
  if (notificationId) {
    return { mode: 'single', notificationId };
  }

  return null;
};

/** Normalize any notification click payload before mark-as-read handling. */
export const preparePortalNotificationRead = (source = {}) => {
  const normalized = normalizePortalNotificationSource(source);
  const readSource = buildPortalNotificationReadSource(normalized);
  const action = resolvePortalNotificationReadAction(readSource);

  return action ? { normalized, readSource, action } : null;
};

export const canMarkPortalNotificationRead = (source = {}) =>
  Boolean(preparePortalNotificationRead(source));
